/**
 * Error handling utilities for Flask API responses
 */

/**
 * Extract user-friendly error message from Flask API response
 * @param {Error} error - The error object from axios/fetch
 * @returns {string} - User-friendly error message
 */
export const getErrorMessage = (error) => {
  // Handle 404 errors specifically
  if (error.response && error.response.status === 404) {
    const url = error.config?.url || error.request?.responseURL || 'unknown';
    return `API endpoint not found (404). The requested endpoint may not be available on the server. URL: ${url}`;
  }
  
  // Check if it's an axios error with response data
  if (error.response && error.response.data) {
    const data = error.response.data;
    
    // Flask returns error messages in different formats
    if (typeof data === 'string') {
      return data;
    }
    
    if (data.error) {
      return typeof data.error === 'string' ? data.error : JSON.stringify(data.error);
    }
    
    if (data.message) {
      return typeof data.message === 'string' ? data.message : JSON.stringify(data.message);
    }
    
    if (data.detail) {
      return typeof data.detail === 'string' ? data.detail : JSON.stringify(data.detail);
    }
    
    // If it's an object with multiple error fields
    if (typeof data === 'object') {
      const errorMessages = [];
      for (const [key, value] of Object.entries(data)) {
        if (key.toLowerCase().includes('error') || key.toLowerCase().includes('message')) {
          if (Array.isArray(value)) {
            errorMessages.push(...value.map(v => typeof v === 'string' ? v : JSON.stringify(v)));
          } else if (typeof value === 'string') {
            errorMessages.push(value);
          } else {
            errorMessages.push(JSON.stringify(value));
          }
        }
      }
      if (errorMessages.length > 0) {
        return errorMessages.join(', ');
      }
    }
  }
  
  // Network or other errors
  if (error.message) {
    if (error.message.includes('Network Error')) {
      return 'Unable to connect to server. Please check your internet connection.';
    }
    if (error.message.includes('timeout')) {
      return 'Request timed out. Please try again.';
    }
    // Ensure error.message is a string
    return typeof error.message === 'string' ? error.message : String(error.message);
  }
  
  // Fallback error message
  return 'An unexpected error occurred. Please try again.';
};

/**
 * Show error alert to user
 * @param {string} message - Error message to display
 * @param {string} type - Alert type (danger, warning, info)
 */
export const showErrorAlert = (message, type = 'danger') => {
  // You can customize this based on your UI library
  // This example uses browser's native alert, but you can replace with your preferred method
  
  // For React Bootstrap Alert component
  if (window.showAlert) {
    window.showAlert(message, type);
    return;
  }
  
  // Fallback to browser alert
  alert(`Error: ${message}`);
};

/**
 * Handle API errors with user-friendly alerts
 * @param {Error} error - The error object from axios/fetch
 * @param {string} defaultMessage - Default message if no specific error found
 */
export const handleApiError = (error, defaultMessage = 'Something went wrong. Please try again.') => {
  const errorMessage = getErrorMessage(error);
  
  // Log error details as readable text (avoid "Object" in console)
  if (error.response) {
    console.error('API Error Details: status=', error.response.status, 'url=', error.config?.url, 'message=', error.message, 'data=', typeof error.response.data === 'object' ? JSON.stringify(error.response.data) : error.response.data);
  } else {
    console.error('API Error: message=', error.message, 'name=', error.name, 'code=', error.code);
  }
  
  // Use the provided defaultMessage if it's more specific, otherwise use extracted error message
  const finalMessage = defaultMessage && defaultMessage !== 'Something went wrong. Please try again.' 
    ? defaultMessage 
    : (errorMessage || defaultMessage);
  
  showErrorAlert(finalMessage);
};

/**
 * Enhanced axios interceptor for global error handling
 */
export const setupAxiosInterceptors = () => {
  const axios = require('axios');
  
  // Response interceptor for global error handling
  axios.interceptors.response.use(
    (response) => response,
    (error) => {
      // Don't show alerts for 401 (unauthorized) - let components handle login redirects
      if (error.response && error.response.status === 401) {
        return Promise.reject(error);
      }
      
      // Show user-friendly error for other errors
      const errorMessage = getErrorMessage(error);
      showErrorAlert(errorMessage);
      
      return Promise.reject(error);
    }
  );
};

/**
 * Wrapper function for API calls with error handling
 * @param {Function} apiCall - The API function to call
 * @param {string} errorMessage - Custom error message
 * @returns {Promise} - Promise that resolves with data or rejects with handled error
 */
export const withErrorHandling = async (apiCall, errorMessage = 'Operation failed') => {
  try {
    return await apiCall();
  } catch (error) {
    handleApiError(error, errorMessage);
    throw error; // Re-throw so calling code can handle if needed
  }
};
